import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd'
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove'
import SearchIcon from '@mui/icons-material/Search'
import SearchOffIcon from '@mui/icons-material/SearchOff'
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material'
import { getLocaleDateString } from 'common/libs/fechas'
import { Fragment, useEffect, useState } from 'react'
import useFetchPurchaseOrderLines from '../hooks/useFetchPurchaseOrderLines'
import SelectPurchaseOrder from '../libs/SelectPurchaseOrder'
import PurchaseOrdersLinesTable from './PurchaseOrdersLinesTable'
import PurchaseOrdersTableSkeletons from './skeletons/PurchaseOrdersTableSkeletons'

/**
 * The PurchaseOrdersTable compoenent
 * @component
 *
 * @param {Object} props
 * @param {Array} props.purchaseOrders
 * @param {boolean} props.loader
 * @param {function} props.handleUpdateSelectedLines
 * @param {fuction} props.handleCheckSelectedLines
 * @param {boolean} props.hidePurchaseOrg
 * @param {boolean} props.purchasingOrganizations
 * @returns {JSX.Element}
 */

const PurchaseOrdersTable = ({
  purchaseOrders,
  loader,
  handleUpdateSelectedLines,
  handleCheckSelectedLines,
  hidePurchaseOrg,
  purchasingOrganizations,
}) => {
  const [showLines, setShowLines] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [purchaseOrderLines, setPurchaseOrderLines] = useState([])

  const { response, loading, reFetch } = useFetchPurchaseOrderLines({ id: selectedOrder })

  useEffect(() => {
    if (!loading) {
      setPurchaseOrderLines(response)
      handleCheckSelectedLines(selectedOrder, response)
    } else {
      setPurchaseOrderLines([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  const devuelveDescripcionOrganizacionCompras = (organizacionCompras) => {
    const d_organizacionCompras = purchasingOrganizations.find(
      (orgCompras) => orgCompras.codigo_org_compras === organizacionCompras,
    )

    if (d_organizacionCompras?.nombre) {
      return d_organizacionCompras.nombre
    } else return organizacionCompras
  }

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: '80vh',
        }}
      >
        <Table sx={{ minWidth: 650 }} stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ px: 2, py: 1, width: 'auto', whiteSpace: 'nowrap' }} align="left">
                Acciones
              </TableCell>
              <TableCell sx={{ px: 2, py: 1 }} align="right">
                Serie
              </TableCell>
              <TableCell sx={{ px: 2, py: 1 }} align="right">
                Número
              </TableCell>
              {hidePurchaseOrg ? (
                <></>
              ) : (
                <TableCell sx={{ px: 2, py: 1 }} align="right">
                  Organización Compras
                </TableCell>
              )}
              <TableCell sx={{ px: 2, py: 1 }} align="right">
                Importe Total Entregado
              </TableCell>
              <TableCell sx={{ px: 2, py: 1 }} align="right">
                Importe Total Facturado
              </TableCell>
              <TableCell sx={{ px: 2, py: 1 }} align="right">
                Importe Total Pedido
              </TableCell>
              <TableCell sx={{ px: 2, py: 1 }} align="right">
                Fecha Grabación
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loader && <PurchaseOrdersTableSkeletons rows={7} columns={8} />}
            {!loader &&
              purchaseOrders?.map((row) => (
                <Fragment key={row.id}>
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={row.id + 'data'}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: 'inherit',
                    }}
                  >
                    <TableCell
                      aling="right"
                      sx={{ px: 2, py: 1, width: '1vh', whiteSpace: 'nowrap' }}
                    >
                      <Tooltip
                        title={
                          row?.has_selected_lines
                            ? 'Deseleccionar lineas de pedido'
                            : 'Seleccionar lineas de pedido'
                        }
                      >
                        <Button
                          disabled={loading}
                          onClick={(e) => {
                            SelectPurchaseOrder.toggleOrder(row.id).then(() => {
                              handleUpdateSelectedLines(row.id)
                              if (showLines && selectedOrder === row.id) {
                                reFetch()
                              }
                            })
                          }}
                        >
                          {row.has_selected_lines ? <PlaylistRemoveIcon /> : <PlaylistAddIcon />}
                        </Button>
                      </Tooltip>
                      <Tooltip
                        title={
                          showLines && selectedOrder === row.id ? 'Ocultar lineas' : 'Ver lineas'
                        }
                      >
                        <Button
                          disabled={loading}
                          onClick={(e) => {
                            if (selectedOrder !== row.id) {
                              setPurchaseOrderLines([])
                              setSelectedOrder(row.id)
                              setShowLines(true)
                            } else {
                              setShowLines(!showLines)
                            }
                          }}
                        >
                          {showLines && selectedOrder === row.id ? (
                            <SearchOffIcon />
                          ) : (
                            <SearchIcon />
                          )}
                        </Button>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ px: 2, py: 1 }}>
                      {row.serie_numeracion}
                    </TableCell>
                    <TableCell align="right" sx={{ px: 2, py: 1 }}>
                      {row.numero_pedido}
                    </TableCell>
                    {hidePurchaseOrg ? (
                      <></>
                    ) : (
                      <TableCell align="right" sx={{ px: 2, py: 1 }}>
                        {devuelveDescripcionOrganizacionCompras(row.organizacion_compras)}
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ px: 2, py: 1 }}>
                      {row.importe_total_entregado}
                    </TableCell>
                    <TableCell align="right" sx={{ px: 2, py: 1 }}>
                      {row.importe_total_facturado}
                    </TableCell>
                    <TableCell align="right" sx={{ px: 2, py: 1 }}>
                      {row.importe_total_pedido}
                    </TableCell>
                    <TableCell align="right" sx={{ px: 2, py: 1 }}>
                      {getLocaleDateString(row.fecha_pedido)}
                    </TableCell>
                  </TableRow>
                  {showLines && selectedOrder === row?.id && (
                    <TableRow key={row.id + 'lines'}>
                      <TableCell colSpan="8">
                        <PurchaseOrdersLinesTable
                          purchaseOrderLines={purchaseOrderLines}
                          loader={loading}
                          reFetch={reFetch}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default PurchaseOrdersTable
